import {
  Attachment,
  ExpandMoreRounded,
  OpenInNewRounded,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EpisodeResources, TruncatedBox } from '@/components';
import { ComplianceTypes, Modals } from '@/constants';
import { Link } from '@/containers';
import { lumiqWhiteLogo, visualAidOverlay } from '@/images';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  selectEpisodeCompliance,
  selectSessionUserId,
  uiActions,
  useAppSelector,
} from '@/store';
import { Episode, EpisodeResource } from '@/types';

import { CPEDetailsModal } from '..';

const VISUAL_AID_ICON_SIZE = {
  width: 86,
  height: 60,
};

export const AdditionalResources = ({ episode }: { episode: Episode }) => {
  const { resources, visualAid, CPEDetails } = episode;
  const visualAidUrl = visualAid?.horizontalPDF?.url;

  const dispatch = useDispatch();

  const userId = useSelector(selectSessionUserId);
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episode?.episodeId),
  );

  const [showCPEDetails, setShowCPEDetails] = useState(false);

  if (
    !resources?.length &&
    !visualAidUrl &&
    !(compliance === ComplianceTypes.CPE && !!CPEDetails)
  )
    return null;

  const onVisualAidClick = () => {
    if (!userId) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SIGN_UP,
          params: { referrer: { button: EVENT_CONSTANTS.BUTTON.VISUAL_AID } },
        }),
      );
      return;
    }
    window.open(visualAidUrl);
    AnalyticsService.visualAidView({
      episodeId: episode.episodeId,
    });
  };

  const onResourceClick = (resource: EpisodeResource) => {
    if (!userId) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SIGN_UP,
          params: {
            referrer: { button: EVENT_CONSTANTS.BUTTON.EPISODE_RESOURCE },
          },
        }),
      );
      return;
    }
    window.open(resource.file.url);
    AnalyticsService.resourceView({
      resourceName: resource.name,
      episodeId: episode.episodeId,
    });
  };

  const onCPEDetailsClick = () => setShowCPEDetails(true);

  return (
    <>
      <TruncatedBox
        ExpandIcon={({ isTruncated }) => (
          <ExpandMoreRounded
            sx={{
              fontSize: 46,
              transition: 'transform 0.2s',
              transform: isTruncated ? null : 'rotate(180deg)',
            }}
          />
        )}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Additional Resources
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1.5,
          }}>
          {visualAidUrl && (
            <Box onClick={onVisualAidClick} sx={styles.visualAid}>
              <Box sx={styles.visualAidIcon}>
                <Box
                  component="img"
                  aria-hidden="true"
                  src={episode.cover}
                  sx={styles.visualAidIconContent}
                />
                <Box
                  component="img"
                  src={visualAidOverlay}
                  aria-hidden="true"
                  sx={styles.visualAidIconContent}
                />
                <Box
                  component="img"
                  src={lumiqWhiteLogo}
                  aria-hidden="true"
                  sx={styles.visualAidLogo}
                />
              </Box>
              <Box>
                <Typography variant="body2SemiBold">Visual Aid</Typography>
                <Typography variant="body3">
                  Additional context and visual supplements
                </Typography>
              </Box>
              <OpenInNewRounded
                sx={[
                  {
                    color: 'secondary.main',
                    width: 30,
                    height: 30,
                    ml: 'auto',
                  },
                ]}
              />
            </Box>
          )}
          {compliance === ComplianceTypes.CPE && !!CPEDetails && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Attachment sx={{ color: 'secondary.main' }} />
              <Link onClick={onCPEDetailsClick}>CPE/QAS Requirements</Link>
            </Box>
          )}
          <EpisodeResources
            resources={resources}
            onResourceClick={onResourceClick}
            contrast="light"
          />
        </Box>
      </TruncatedBox>
      <CPEDetailsModal
        isOpened={showCPEDetails}
        setOpened={setShowCPEDetails}
        CPEDetails={CPEDetails!}
      />
    </>
  );
};

const styles = {
  resourceLink: {
    display: 'flex',
    alignItems: 'center',
    color: 'secondary.main',
  },
  visualAid: {
    backgroundColor: 'grey50',
    px: { xs: 1, md: 2 },
    py: 2,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1, md: 2 },
    cursor: 'pointer',
    width: '100%',
    '&:hover': {
      backgroundColor: 'grey75',
    },
  },
  visualAidIcon: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 1,
    flexShrink: 0,
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.30)',
    ...VISUAL_AID_ICON_SIZE,
  },
  visualAidIconContent: {
    position: 'absolute',
    ...VISUAL_AID_ICON_SIZE,
  },
  visualAidLogo: {
    position: 'absolute',
    width: 32,
    top: 4,
    left: 4,
  },
};
